import React, { createContext, useState, useEffect, useContext, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  fetchPortfolios,
  getPortfolioSummary,
  createNewPortfolio,
  analyzePortfolioHealth,
  recalculatePortfolioSummary,
} from '../services/api';
import toast from 'react-hot-toast';

// --- TYPE DEFINITIONS ---
interface Portfolio {
  id: string;
  name: string;
  description?: string;
  [key: string]: any;
}

interface PortfolioSummary {
  [key: string]: any;
}

interface HealthCheckResult {
  new_credit_balance?: number;
  [key: string]: any;
}

interface PortfolioContextType {
  portfolios: Portfolio[];
  activePortfolio: Portfolio | undefined;
  activePortfolioId: string | null;
  switchActivePortfolio: (id: string) => void;
  isLoading: boolean;
  error: string | null;
  activePortfolioSummary: PortfolioSummary | null;
  isSummaryLoading: boolean;
  createPortfolio: (name: string, description: string) => Promise<Portfolio>;
  removePortfolioFromState: (portfolioId: string) => void;
  refreshPortfolios: () => Promise<void>;
  healthCheckResult: HealthCheckResult | null;
  isAnalyzing: boolean;
  analyzePortfolio: () => Promise<void>;
  isCalculating: boolean;
  setIsCalculating: (calculating: boolean) => void;
  handleRecalculate: () => Promise<void>;
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(undefined);

// --- CUSTOM HOOK ---
export function usePortfolio(): PortfolioContextType {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
}

// --- PROVIDER ---
interface PortfolioProviderProps {
  children: ReactNode;
  location?: any;
}

export function PortfolioProvider({ children }: PortfolioProviderProps) {
  const { currentUser, setAiCredits } = useAuth();
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [activePortfolioId, setActivePortfolioId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activePortfolioSummary, setActivePortfolioSummary] = useState<PortfolioSummary | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [healthCheckResult, setHealthCheckResult] = useState<HealthCheckResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // Refresh portfolios list
  const refreshPortfolios = useCallback(async () => {
    if (!currentUser) {
      setPortfolios([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const data = await fetchPortfolios(token);
      const fetchedPortfolios = data.portfolios || [];
      setPortfolios(fetchedPortfolios);
      if (fetchedPortfolios.length > 0 && !activePortfolioId) {
        setActivePortfolioId(fetchedPortfolios[0].id);
      }
    } catch (err: any) {
      setError('Failed to load portfolios list.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, activePortfolioId]);

  // Analyze portfolio health with AI
  const analyzePortfolio = async () => {
    if (!currentUser) throw new Error('You must be logged in.');

    setIsAnalyzing(true);
    setHealthCheckResult(null);
    const toastId = toast.loading('AI is analyzing your portfolio health...');

    try {
      const token = await currentUser.getIdToken();
      const result = await analyzePortfolioHealth(token);

      setHealthCheckResult(result);
      if (typeof result.new_credit_balance === 'number') {
        setAiCredits(result.new_credit_balance);
      }
      toast.success('Health check complete!', { id: toastId });
    } catch (error: any) {
      toast.error(`Analysis failed: ${error.message}`, { id: toastId });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Initial load
  useEffect(() => {
    refreshPortfolios();
  }, [refreshPortfolios]);

  // Load summary when active portfolio changes
  useEffect(() => {
    if (!activePortfolioId || !currentUser) {
      setActivePortfolioSummary(null);
      return;
    }
    const loadSummaryData = async () => {
      setIsSummaryLoading(true);
      try {
        const token = await currentUser.getIdToken();
        const summaryData = await getPortfolioSummary(token, activePortfolioId);
        setActivePortfolioSummary(summaryData);
      } catch (err: any) {
        // Silently handle 403/auth errors - common for new users or stale data
        const msg = err?.message || '';
        if (msg.includes('Forbidden') || msg.includes('Unauthorized') || msg.includes('not found')) {
          console.log('Portfolio summary not available yet for this portfolio.');
        } else {
          console.error('Failed to load portfolio summary:', err);
        }
        setActivePortfolioSummary(null);
      } finally {
        setIsSummaryLoading(false);
      }
    };
    loadSummaryData();
  }, [activePortfolioId, currentUser]);

  // Create new portfolio
  const createPortfolio = async (name: string, description: string): Promise<Portfolio> => {
    if (!currentUser) throw new Error('You must be logged in.');

    const token = await currentUser.getIdToken();
    const portfolioData = { name, description };

    const newPortfolioFromServer = await createNewPortfolio(token, portfolioData);

    setPortfolios((prev) => [...prev, newPortfolioFromServer]);
    setActivePortfolioId(newPortfolioFromServer.id);

    return newPortfolioFromServer;
  };

  // Remove portfolio from state
  const removePortfolioFromState = (portfolioIdToRemove: string) => {
    const updatedPortfolios = portfolios.filter((p) => p.id !== portfolioIdToRemove);
    setPortfolios(updatedPortfolios);

    if (activePortfolioId === portfolioIdToRemove) {
      const newActiveId = updatedPortfolios.length > 0 ? updatedPortfolios[0].id : null;
      setActivePortfolioId(newActiveId);
    }
  };

  // Switch active portfolio
  const switchActivePortfolio = (id: string) => {
    setActivePortfolioId(id);
    setActivePortfolioSummary(null);
  };

  // Get active portfolio object
  const activePortfolio = portfolios.find((p) => p.id === activePortfolioId);

  // Recalculate portfolio
  const handleRecalculate = useCallback(async () => {
    if (!currentUser || !activePortfolio) return;

    const toastId = toast.loading(`Requesting recalculation for ${activePortfolio.name}...`);
    try {
      const token = await currentUser.getIdToken();
      await recalculatePortfolioSummary(token, activePortfolio.id);

      toast.success('Recalculation started! The page will now refresh.', { id: toastId });

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      toast.error(`Request failed: ${error.message}`, { id: toastId });
    }
  }, [currentUser, activePortfolio]);

  const value: PortfolioContextType = {
    portfolios,
    activePortfolio,
    activePortfolioId,
    switchActivePortfolio,
    isLoading,
    error,
    activePortfolioSummary,
    isSummaryLoading,
    createPortfolio,
    removePortfolioFromState,
    refreshPortfolios,
    healthCheckResult,
    isAnalyzing,
    analyzePortfolio,
    isCalculating,
    setIsCalculating,
    handleRecalculate,
  };

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
}

